// WP-3.1 (M1 PT-1..6, S-19 `/parties`): search-first list — name, roles chips, archived
// filter; ＋ Add; ⋮ Merge tool (pick two -> field-by-field keep-picker -> preview -> merge,
// PT-4). "Net position with me" / "last activity" columns from S-19's own field list are a
// scope trim — computing them for every row would need a NEW bulk engine query to avoid an
// N-profile-request fan-out per page load; S-20's own profile page is where those numbers
// live today, one click away.
import { PARTY_ROLES, type PartyRole } from '@pfos/engine';
import type { PartyResponse } from '@pfos/shared';
import { Plus, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { checkboxClass, Field, ghostButtonInlineClass, inputClass } from '../components/FormKit';
import { FormSheet } from '../components/FormSheet';
import { ApiError } from '../lib/api';
import { useCreateParty, useMergeParties, useParties } from '../lib/parties';

const ROLE_LABELS: Record<PartyRole, string> = {
  client: 'Client',
  vendor: 'Vendor',
  friend: 'Friend',
  family: 'Family',
  freelancer: 'Freelancer',
  employee: 'Employee',
  business_partner: 'Business partner',
  creditor: 'Creditor',
  debtor: 'Debtor',
  tpf_owner: 'TPF owner',
  custom: 'Custom',
};

function RoleChips({ roles }: { roles: PartyRole[] }): React.JSX.Element | null {
  if (roles.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span key={r} className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
          {ROLE_LABELS[r]}
        </span>
      ))}
    </span>
  );
}

function NewPartyForm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<PartyRole[]>([]);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateParty();

  useEffect(() => {
    if (open) {
      setName('');
      setRoles([]);
      setPhone('');
      setError(null);
    }
  }, [open]);

  const toggleRole = (role: PartyRole): void => {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="New party"
      submitLabel="Save"
      submitting={createMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (name.trim() === '') {
          setError('Name is required');
          return;
        }
        createMutation.mutate(
          {
            name: name.trim(),
            ...(roles.length > 0 ? { roles } : {}),
            ...(phone.trim() !== '' ? { phone: phone.trim() } : {}),
          },
          {
            onSuccess: () => {
              onOpenChange(false);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : 'Could not save');
            },
          },
        );
      }}
    >
      {error !== null && <p className="mb-3 text-sm text-danger">{error}</p>}
      <Field label="Name" htmlFor="party-name">
        <input
          id="party-name"
          className={inputClass}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </Field>
      <Field label="Phone (optional)" htmlFor="party-phone">
        <input
          id="party-phone"
          className={inputClass}
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
          }}
        />
      </Field>
      <fieldset className="mb-3">
        <legend className="mb-1 block text-sm text-muted">
          Roles (PT-1: pick as many as apply)
        </legend>
        <div className="flex flex-wrap gap-3">
          {PARTY_ROLES.map((role) => (
            <label key={role} className="flex items-center gap-1.5 text-sm text-ink">
              <input
                type="checkbox"
                className={checkboxClass}
                checked={roles.includes(role)}
                onChange={() => {
                  toggleRole(role);
                }}
              />
              {ROLE_LABELS[role]}
            </label>
          ))}
        </div>
      </fieldset>
    </FormSheet>
  );
}

type MergeField = 'name' | 'phone';

function MergeTool({
  open,
  onOpenChange,
  parties,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parties: PartyResponse[];
}): React.JSX.Element {
  const [aId, setAId] = useState('');
  const [bId, setBId] = useState('');
  const [keepIsA, setKeepIsA] = useState(true);
  const [fieldFromA, setFieldFromA] = useState<Record<MergeField, boolean>>({
    name: true,
    phone: true,
  });
  const [error, setError] = useState<string | null>(null);
  const mergeMutation = useMergeParties();

  useEffect(() => {
    if (open) {
      setAId('');
      setBId('');
      setKeepIsA(true);
      setFieldFromA({ name: true, phone: true });
      setError(null);
    }
  }, [open]);

  const a = parties.find((p) => p.id === aId);
  const b = parties.find((p) => p.id === bId);
  const keep = keepIsA ? a : b;
  const merge = keepIsA ? b : a;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Merge duplicate parties"
      submitLabel="Merge"
      submitting={mergeMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (a === undefined || b === undefined) {
          setError('Choose two parties');
          return;
        }
        if (a.id === b.id) {
          setError('Choose two different parties');
          return;
        }
        if (keep === undefined || merge === undefined) return;
        mergeMutation.mutate(
          {
            keepId: keep.id,
            mergeId: merge.id,
            name: fieldFromA.name ? a.name : b.name,
            ...(fieldFromA.phone
              ? a.phone !== undefined
                ? { phone: a.phone }
                : {}
              : b.phone !== undefined
                ? { phone: b.phone }
                : {}),
          },
          {
            onSuccess: () => {
              onOpenChange(false);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : 'Could not merge');
            },
          },
        );
      }}
    >
      {error !== null && <p className="mb-3 text-sm text-danger">{error}</p>}
      <Field label="Party A" htmlFor="merge-a">
        <select
          id="merge-a"
          className={inputClass}
          value={aId}
          onChange={(e) => {
            setAId(e.target.value);
          }}
        >
          <option value="">Select a party</option>
          {parties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Party B" htmlFor="merge-b">
        <select
          id="merge-b"
          className={inputClass}
          value={bId}
          onChange={(e) => {
            setBId(e.target.value);
          }}
        >
          <option value="">Select a party</option>
          {parties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>

      {a !== undefined && b !== undefined && a.id !== b.id && (
        <div className="mt-4 rounded-[var(--radius-control)] border border-border p-3">
          <p className="mb-2 text-sm font-medium text-ink">Field-by-field keep-picker</p>
          <label className="mb-2 flex items-center gap-2 text-sm text-ink">
            <input
              type="radio"
              name="merge-primary"
              checked={keepIsA}
              onChange={() => {
                setKeepIsA(true);
              }}
            />
            Keep A&apos;s record ({a.name}) as the surviving party
          </label>
          <label className="mb-3 flex items-center gap-2 text-sm text-ink">
            <input
              type="radio"
              name="merge-primary"
              checked={!keepIsA}
              onChange={() => {
                setKeepIsA(false);
              }}
            />
            Keep B&apos;s record ({b.name}) as the surviving party
          </label>

          <div className="mb-2">
            <p className="mb-1 text-xs text-muted">Name</p>
            <label className="mr-4 text-sm text-ink">
              <input
                type="radio"
                name="merge-name"
                checked={fieldFromA.name}
                onChange={() => {
                  setFieldFromA((prev) => ({ ...prev, name: true }));
                }}
              />{' '}
              A: {a.name}
            </label>
            <label className="text-sm text-ink">
              <input
                type="radio"
                name="merge-name"
                checked={!fieldFromA.name}
                onChange={() => {
                  setFieldFromA((prev) => ({ ...prev, name: false }));
                }}
              />{' '}
              B: {b.name}
            </label>
          </div>

          <div className="mb-2">
            <p className="mb-1 text-xs text-muted">Phone</p>
            <label className="mr-4 text-sm text-ink">
              <input
                type="radio"
                name="merge-phone"
                checked={fieldFromA.phone}
                onChange={() => {
                  setFieldFromA((prev) => ({ ...prev, phone: true }));
                }}
              />{' '}
              A: {a.phone ?? '—'}
            </label>
            <label className="text-sm text-ink">
              <input
                type="radio"
                name="merge-phone"
                checked={!fieldFromA.phone}
                onChange={() => {
                  setFieldFromA((prev) => ({ ...prev, phone: false }));
                }}
              />{' '}
              B: {b.phone ?? '—'}
            </label>
          </div>

          <p className="mt-3 text-xs text-muted">
            Preview: {keep?.name} survives as &quot;{fieldFromA.name ? a.name : b.name}&quot;, phone{' '}
            {(fieldFromA.phone ? a.phone : b.phone) ?? '—'}. {merge?.name} is archived and every
            transaction/payable/ receivable/TPF case it was linked to is re-pointed here.
          </p>
        </div>
      )}
    </FormSheet>
  );
}

export function PartiesPage(): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const { data: parties, isLoading } = useParties(showArchived ? undefined : false);

  const filtered = (parties ?? []).filter((p) => {
    if (search.trim() === '') return true;
    const q = search.trim().toLowerCase();
    return p.name.toLowerCase().includes(q) || p.aliases.some((a) => a.toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-ink">Parties</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={ghostButtonInlineClass}
            onClick={() => {
              setMergeOpen(true);
            }}
          >
            Merge tool
          </button>
          <button
            type="button"
            className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
            onClick={() => {
              setFormOpen(true);
            }}
          >
            <Plus size={16} /> Add party
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <input
          className={`${inputClass} max-w-sm`}
          placeholder="Search by name or alias…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <label className="flex items-center gap-1.5 text-sm text-muted">
          <input
            type="checkbox"
            className={checkboxClass}
            checked={showArchived}
            onChange={(e) => {
              setShowArchived(e.target.checked);
            }}
          />
          Show archived
        </label>
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && filtered.length === 0 && (
        <EmptyState
          icon={Users}
          message="No parties yet."
          action={{
            label: 'Add party',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {filtered.length > 0 && (
        <ul className="rounded-[var(--radius-card)] border border-border bg-surface px-4">
          {filtered.map((p) => (
            <li
              key={p.id}
              className={`flex items-center justify-between gap-3 border-b border-border py-3 last:border-0 ${p.archived ? 'opacity-60' : ''}`}
            >
              <Link to={`/parties/${p.id}`} className="flex flex-1 items-center gap-3">
                <span className="text-sm font-medium text-ink">{p.name}</span>
                {p.provisional && (
                  <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs text-warning">
                    Unconfirmed
                  </span>
                )}
                <RoleChips roles={p.roles} />
              </Link>
              {p.archived && <span className="text-xs text-muted">Archived</span>}
            </li>
          ))}
        </ul>
      )}

      <NewPartyForm open={formOpen} onOpenChange={setFormOpen} />
      <MergeTool open={mergeOpen} onOpenChange={setMergeOpen} parties={parties ?? []} />
    </div>
  );
}
